import React, { useState, useEffect, useMemo, Suspense, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { PortfolioData, PoliticianData, TimeFrame, TimeframeData } from './types';
import PerformanceChart from './components/PerformanceChart';
import TopPerformers from './components/TopPerformers';
import PoliticianCard from './components/PoliticianCard';
import ShareButtons from './components/ShareButtons';
import SEOHead, { generateSlug } from './components/SEOHead';
import Leaderboard from './components/Leaderboard';
import News from './components/News';
import './i18n';
import './App.css';

// Generate colors from Gold → Violet gradient
const generateTopColors = (count: number): string[] => {
  const colors: string[] = [];
  // HSL range: Gold (45°, 80%, 50%) → Violet (270°, 50%, 55%)
  const startHue = 45;
  const endHue = 270;
  const hueRange = endHue - startHue;

  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0 : i / (count - 1);
    const hue = startHue + hueRange * t;
    const saturation = 80 - t * 30; // 80% → 50%
    const lightness = 50 + t * 5;   // 50% → 55%
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }

  return colors;
};

// Find politician by slug
const findPoliticianBySlug = (
  politicians: Record<string, PoliticianData>,
  slug: string
): PoliticianData | null => {
  const entries = Object.entries(politicians);
  for (const [, politician] of entries) {
    if (generateSlug(politician.name) === slug) {
      return politician;
    }
  }
  return null;
};

interface MainAppProps {
  metadata: PortfolioData['metadata'];
  currentData: TimeframeData;
  filteredData: TimeframeData;
  selectedTimeFrame: TimeFrame;
  selectedCommittee: string;
  topCount: number;
  loadingTimeframe: boolean;
  loadingMore: boolean;
  onTimeFrameChange: (tf: TimeFrame) => void;
  onCommitteeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onTopCountChange: (count: number) => void;
}

function MainApp({
  metadata,
  currentData,
  filteredData,
  selectedTimeFrame,
  selectedCommittee,
  topCount,
  loadingTimeframe,
  loadingMore,
  onTimeFrameChange,
  onCommitteeChange,
  onTopCountChange,
}: MainAppProps) {
  const { t } = useTranslation();
  const { politicianSlug } = useParams<{ politicianSlug: string }>();
  const navigate = useNavigate();

  // Find selected politician from URL slug
  const selectedPolitician = useMemo(() => {
    if (!politicianSlug || !filteredData) return null;
    return findPoliticianBySlug(filteredData.politicians, politicianSlug);
  }, [politicianSlug, filteredData]);

  const topPerformers = useMemo(() => {
    if (!filteredData) return [];
    return Object.values(filteredData.politicians)
      .sort((a, b) => b.currentValue - a.currentValue)
      .slice(0, topCount);
  }, [filteredData, topCount]);

  const handleSelectPolitician = useCallback((politician: PoliticianData | null) => {
    if (politician) {
      navigate(`/politician/${generateSlug(politician.name)}`);
    } else {
      navigate('/');
    }
  }, [navigate]);

  const handleClose = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleTimeFrameChange = (tf: TimeFrame) => {
    onTimeFrameChange(tf);
    // Keep on same politician page if one is selected
  };

  const handleCommitteeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onCommitteeChange(e);
    // Navigate to home when committee changes as politician may no longer be visible
    navigate('/');
  };

  const filteredCount = Object.keys(filteredData.politicians).length;

  return (
    <div className="app">
      <SEOHead politician={selectedPolitician} timeframe={selectedTimeFrame} />
      <header className="header">
        <div className="header-left">
          <h1 className="title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            {t('appTitle')}
          </h1>
          <p className="subtitle">{t('subtitle')}</p>
          <nav className="header-nav">
            <button className="nav-link active" onClick={() => navigate('/')}>
              {t('nav.dashboard')}
            </button>
            <button className="nav-link" onClick={() => navigate('/leaderboard')}>
              {t('nav.leaderboard')}
            </button>
            <button className="nav-link" onClick={() => navigate('/news')}>
              {t('nav.news')}
            </button>
          </nav>
        </div>
        <div className="header-center">
          <div className="timeframe-selector">
            {metadata.available_timeframes.map((tf) => (
              <button
                key={tf}
                className={`timeframe-btn ${selectedTimeFrame === tf ? 'active' : ''} ${loadingTimeframe ? 'loading' : ''}`}
                onClick={() => handleTimeFrameChange(tf)}
                disabled={loadingTimeframe}
              >
                {tf}
              </button>
            ))}
          </div>
          <div className="committee-selector">
            <select
              value={selectedCommittee}
              onChange={handleCommitteeChange}
              className="committee-dropdown"
            >
              <option value="all">{t('allCommittees')}</option>
              {metadata.committees.map((committee) => (
                <option key={committee} value={committee}>
                  {committee}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="header-right">
          <div className="stat-box">
            <span className="stat-label">{t('politicians')}</span>
            <span className="stat-value">
              {filteredCount}
              {loadingMore && <span className="loading-more-indicator" title="Loading more politicians...">...</span>}
            </span>
          </div>
          <div className="stat-box">
            <span className="stat-label">{t('initial')}</span>
            <span className="stat-value">$10,000</span>
          </div>
          <ShareButtons />
        </div>
      </header>

      <main className="main">
        <div className={`chart-container ${selectedPolitician ? 'with-sidebar' : ''}`}>
          <PerformanceChart
            data={filteredData}
            selectedPolitician={selectedPolitician}
            onSelectPolitician={handleSelectPolitician}
          />
        </div>

        {selectedPolitician && (
          <PoliticianCard
            politician={selectedPolitician}
            benchmarks={filteredData.benchmarks}
            timeframe={selectedTimeFrame}
            onClose={handleClose}
          />
        )}
      </main>

      <footer className="footer">
        <TopPerformers
          topPerformers={topPerformers}
          onSelect={handleSelectPolitician}
          selectedId={selectedPolitician?.id}
          topCount={topCount}
          onTopCountChange={onTopCountChange}
        />
      </footer>
    </div>
  );
}

function App() {
  const { t } = useTranslation();
  const [metadata, setMetadata] = useState<PortfolioData['metadata'] | null>(null);
  const [currentData, setCurrentData] = useState<TimeframeData | null>(null);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<TimeFrame>('1Y');
  const [selectedCommittee, setSelectedCommittee] = useState<string>('all');
  const [topCount, setTopCount] = useState<number>(5);
  const [loading, setLoading] = useState(true);
  const [loadingTimeframe, setLoadingTimeframe] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load metadata on mount
  useEffect(() => {
    fetch('/data/timeseries/metadata.json')
      .then(res => res.json())
      .then((meta: PortfolioData['metadata']) => {
        setMetadata(meta);
        // Set default timeframe from metadata
        if (meta?.default_timeframe) {
          setSelectedTimeFrame(meta.default_timeframe);
        }
      })
      .catch(err => {
        console.error('Failed to load metadata:', err);
        setLoading(false);
      });
  }, []);

  // Load timeframe data when timeframe changes (top 20 first, then full data)
  useEffect(() => {
    if (!metadata) return;

    const loadData = async () => {
      setLoadingTimeframe(true);

      try {
        // First, try to load top20 file for fast initial render
        const top20Response = await fetch(`/data/timeseries/${selectedTimeFrame}_top20.json`);

        if (top20Response.ok) {
          const top20Data: TimeframeData = await top20Response.json();
          setCurrentData(top20Data);
          setLoading(false);
          setLoadingTimeframe(false);
          setLoadingMore(true);

          // Background load full data
          try {
            const fullResponse = await fetch(`/data/timeseries/${selectedTimeFrame}.json`);
            if (fullResponse.ok) {
              const fullData: TimeframeData = await fullResponse.json();
              setCurrentData(fullData);
            }
          } catch (err) {
            console.error(`Failed to load full ${selectedTimeFrame} data:`, err);
          } finally {
            setLoadingMore(false);
          }
        } else {
          // Fallback to full file if top20 doesn't exist
          const fullResponse = await fetch(`/data/timeseries/${selectedTimeFrame}.json`);
          const fullData: TimeframeData = await fullResponse.json();
          setCurrentData(fullData);
          setLoading(false);
          setLoadingTimeframe(false);
        }
      } catch (err) {
        console.error(`Failed to load ${selectedTimeFrame} data:`, err);
        setLoading(false);
        setLoadingTimeframe(false);
        setLoadingMore(false);
      }
    };

    loadData();
  }, [selectedTimeFrame, metadata]);

  // Filter politicians by committee and apply dynamic coloring
  const filteredData: TimeframeData | null = useMemo(() => {
    if (!currentData) return null;

    // Filter politicians by committee
    let politicians = Object.values(currentData.politicians);
    if (selectedCommittee !== 'all') {
      politicians = politicians.filter(p =>
        p.committees && p.committees.includes(selectedCommittee)
      );
    }

    // Sort by performance and assign colors to top performers
    const sorted = [...politicians].sort((a, b) => b.currentValue - a.currentValue);
    const coloredPoliticians: Record<string, PoliticianData> = {};
    const topColors = generateTopColors(topCount);

    sorted.forEach((p, i) => {
      const color = i < topCount ? topColors[i] : '#9ca3af';
      coloredPoliticians[p.name] = {
        ...p,
        color,
        isTopPerformer: i < topCount,
        isBottomPerformer: false,
        isMedian: false,
      };
    });

    return {
      politicians: coloredPoliticians,
      benchmarks: currentData.benchmarks,
    };
  }, [currentData, selectedCommittee, topCount]);

  const handleTimeFrameChange = (tf: TimeFrame) => {
    setSelectedTimeFrame(tf);
  };

  const handleCommitteeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCommittee(e.target.value);
  };

  if (loading) {
    return (
      <div className="app loading">
        <div className="loader">
          <div className="loader-text">{t('loading')}</div>
          <div className="loader-bar">
            <div className="loader-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!metadata || !currentData || !filteredData) {
    return (
      <div className="app error">
        <div className="error-text">{t('error')}</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <MainApp
            metadata={metadata}
            currentData={currentData}
            filteredData={filteredData}
            selectedTimeFrame={selectedTimeFrame}
            selectedCommittee={selectedCommittee}
            topCount={topCount}
            loadingTimeframe={loadingTimeframe}
            loadingMore={loadingMore}
            onTimeFrameChange={handleTimeFrameChange}
            onCommitteeChange={handleCommitteeChange}
            onTopCountChange={setTopCount}
          />
        }
      />
      <Route
        path="/politician/:politicianSlug"
        element={
          <MainApp
            metadata={metadata}
            currentData={currentData}
            filteredData={filteredData}
            selectedTimeFrame={selectedTimeFrame}
            selectedCommittee={selectedCommittee}
            topCount={topCount}
            loadingTimeframe={loadingTimeframe}
            loadingMore={loadingMore}
            onTimeFrameChange={handleTimeFrameChange}
            onCommitteeChange={handleCommitteeChange}
            onTopCountChange={setTopCount}
          />
        }
      />
      <Route
        path="/leaderboard"
        element={
          <Leaderboard data={filteredData} />
        }
      />
      <Route path="/news" element={<News />} />
      <Route path="/news/:slug" element={<News />} />
    </Routes>
  );
}

export default function AppWithProviders() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Suspense fallback={
          <div className="app loading">
            <div className="loader">
              <div className="loader-text">Loading...</div>
              <div className="loader-bar">
                <div className="loader-progress"></div>
              </div>
            </div>
          </div>
        }>
          <App />
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  );
}
